/**
 * P2-10: CraftRow —— 合成表行（迁移自 WCraftRow，js/jsx/left.jsx.js:417-558）。
 *
 * 显示条件逐条对齐 INVENTORY "合成行"（left.jsx.js:528-538）：
 * 1. res.unlocked && game.workshop.getCraft(res.name).unlocked；
 * 2. 可见性：wood 特判 !res.isHiddenFromCrafting（同时出现在主表与合成表，
 *    双标志位，resources.js:619-622），其余 !res.isHidden；
 * 3. isEditMode 无视隐藏标记全部显示。
 *
 * 隐藏标记直接改 res.isHidden/isHiddenFromCrafting（left.jsx.js:506-513），
 * **不走** resPool.setResourceIsHidden（与主表行不同）；
 * onClickName 只认 ctrlKey（无 metaKey，对齐旧版 left.jsx.js:500-504）。
 *
 * 旧 shouldComponentUpdate 短路（left.jsx.js:426-437）转化为行级 useGameTick
 * 精确选择器：craft 行只消费真实资源（resPool.resources 中 res.craftable 项，
 * 对象原地变异引用稳定），快照按字段比较，不可见返回常量 {shown:false}。
 *
 * 数值格 tooltip 复用 P2-06 门控 + 引擎 getDetailedResMap（left.jsx.js:540-557
 * 同样挂 attachResourceTooltip，锚点为 resource-value 格）。
 *
 * 快捷链四列（+1/+25/+100/all）由 CraftShortcut 承担（P2-11，left.jsx.js:494-497）。
 */
import { useCallback } from "react";
import type { CSSProperties, MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { TooltipAnchor } from "../../components/Tooltip/TooltipProvider";
import { getResourceTooltipContent } from "./resourceTooltip";
import { CraftShortcut } from "./CraftShortcut";
import type { ResLike } from "./ResourceRow";

// ---------------------------------------------------------------------------
// 可见性（纯函数，导出供测试与 CraftTable 复用）
// ---------------------------------------------------------------------------

/** wood 双标志位特判（left.jsx.js:517-524） */
export function isCraftRowVisibleFlag(res: ResLike & { isHiddenFromCrafting?: boolean }): boolean {
  if (res.name == "wood") {
    //(Wood is special because it appears twice, separately)
    return !res.isHiddenFromCrafting;
  }
  return !res.isHidden;
}

/** 逐条对齐旧 getIsResInCraftTable（left.jsx.js:528-538） */
export function isResInCraftTable(
  game: GamePage,
  res: ResLike,
  isEditMode: boolean,
): boolean {
  const recipe = game.workshop.getCraft(res.name);
  const hasVisibility = res.unlocked && recipe.unlocked;
  if (!hasVisibility || (!isCraftRowVisibleFlag(res) && !isEditMode)) {
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// 行快照
// ---------------------------------------------------------------------------

interface HiddenSnapshot {
  shown: false;
}

interface VisibleSnapshot {
  shown: true;
  /** res.title || res.name（名称格文本与原生 title 同值，left.jsx.js:489） */
  title: string;
  /** 隐藏标志位（wood → isHiddenFromCrafting），编辑复选框 checked 用 */
  hiddenFlag: boolean;
  /** 数值格文本（同时作为该格原生 title，left.jsx.js:493） */
  valueText: string;
  /** 工程师加成 → " craftEngineer "（left.jsx.js:474） */
  engineer: boolean;
  /** 名称格行内样式（同 ResourceRow 的逐分支覆盖语义） */
  cssColor?: string;
  cssTextShadow?: string;
  styleRef?: Record<string, unknown>;
}

export type CraftRowSnapshot = HiddenSnapshot | VisibleSnapshot;

function snapshotEq(a: CraftRowSnapshot | null, b: CraftRowSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.shown !== b.shown) {
    return false;
  }
  if (!a.shown || !b.shown) {
    return true;
  }
  return (
    a.title === b.title &&
    a.hiddenFlag === b.hiddenFlag &&
    a.valueText === b.valueText &&
    a.engineer === b.engineer &&
    a.cssColor === b.cssColor &&
    a.cssTextShadow === b.cssTextShadow &&
    a.styleRef === b.styleRef
  );
}

export function computeCraftRowSnapshot(
  game: GamePage | null,
  name: string,
  isEditMode: boolean,
): CraftRowSnapshot | null {
  if (!game) {
    return null;
  }
  const res = game.resPool.get(name) as ResLike | false;
  if (!res || !isResInCraftTable(game, res, isEditMode)) {
    return { shown: false };
  }

  // 名称颜色（left.jsx.js:450-471，与主表行同一套逐分支覆盖）
  let cssColor: string | undefined;
  let cssTextShadow: string | undefined;
  if (res.type == "uncommon") {
    cssColor = "Coral";
  }
  if (res.type == "rare") {
    cssColor = "orange";
    cssTextShadow = "1px 0px 10px Coral";
  }
  if (res.color) {
    cssColor = res.color;
    cssTextShadow = undefined;
  }

  return {
    shown: true,
    title: res.title || res.name,
    hiddenFlag: !isCraftRowVisibleFlag(res),
    valueText: game.getDisplayValueExt(res.value),
    engineer: game.workshop.getEffectEngineer(res.name) != 0,
    cssColor,
    cssTextShadow,
    styleRef: res.style,
  };
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export interface CraftRowProps {
  /** 资源名（craft 行只消费真实资源） */
  name: string;
  isEditMode: boolean;
  isRequired: boolean;
}

export function CraftRow({ name, isEditMode, isRequired }: CraftRowProps) {
  const selector = useCallback(
    (game: GamePage | null) => computeCraftRowSnapshot(game, name, isEditMode),
    [name, isEditMode],
  );
  const snap = useGameTick(selector, snapshotEq);

  // 直接改 res.isHidden/isHiddenFromCrafting（left.jsx.js:506-513），不走 setResourceIsHidden
  const toggleView = useCallback(() => {
    const res = getGame()?.resPool.get(name) as (ResLike & { isHiddenFromCrafting?: boolean }) | false;
    if (!res) {
      return;
    }
    if (res.name == "wood") {
      res.isHiddenFromCrafting = !res.isHiddenFromCrafting;
    } else {
      res.isHidden = !res.isHidden;
    }
  }, [name]);

  if (!snap || !snap.shown) {
    return null;
  }

  // 合并顺序对齐旧版：type/color 为基底，res.style 的键最后覆盖
  const nameCss: CSSProperties = {};
  if (snap.cssColor) {
    nameCss.color = snap.cssColor;
  }
  if (snap.cssTextShadow) {
    nameCss.textShadow = snap.cssTextShadow;
  }
  Object.assign(nameCss, snap.styleRef);

  const rowClass =
    "res-row craft resource_" + name +
    (snap.engineer ? " craftEngineer " : "") +
    (isRequired ? " highlited" : "");

  return (
    <div className={rowClass}>
      {isEditMode && (
        <div className="res-cell">
          <input
            type="checkbox"
            checked={!snap.hiddenFlag}
            onChange={toggleView}
            style={{ display: "inline-block" }}
          />
        </div>
      )}
      <div
        className="res-cell resource-name"
        style={nameCss}
        onClick={(e: MouseEvent) => {
          // 对齐旧版：只认 ctrlKey（无 metaKey，left.jsx.js:500-504）
          if (isEditMode || e.ctrlKey) {
            toggleView();
          }
        }}
        title={snap.title}
      >
        {snap.title}
      </div>
      <TooltipAnchor
        className="res-cell resource-value"
        title={snap.valueText}
        activeStyle={{ fontWeight: "bold" }}
        getTooltip={() => {
          const game = getGame();
          const res = game ? (game.resPool.get(name) as ResLike | false) : null;
          return game && res ? getResourceTooltipContent(game, res) : "";
        }}
      >
        {snap.valueText}
      </TooltipAnchor>
      {/* 四列固定档位（left.jsx.js:494-497） */}
      <CraftShortcut name={name} craftFixed={1} craftPercent={0.01} />
      <CraftShortcut name={name} craftFixed={25} craftPercent={0.05} />
      <CraftShortcut name={name} craftFixed={100} craftPercent={0.1} />
      <CraftShortcut name={name} craftPercent={1} />
    </div>
  );
}
